import { 
  CookieConsent, 
  ConsentUpdate, 
  ConsentValidation,
  ConsentCategory,
  ConsentStatus 
} from '@/types/consent';
import { 
  COOKIE_CONSENT_KEY, 
  CONSENT_EXPIRY_DAYS, 
  DEFAULT_CONSENT,
  CONSENT_VERSION 
} from '@/config/analytics';

/**
 * Cookie Consent Manager - Handles localStorage operations for consent data
 */
export class CookieConsentManager {
  /**
   * Get current consent from localStorage
   * Returns default consent if none exists or if expired
   */
  static getConsent(): CookieConsent {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!stored) {
        // No stored consent, return default
        return this.getDefaultConsent();
      }

      const consent: CookieConsent = JSON.parse(stored);
      
      // Validate the consent object
      const validation = this.validateConsent(consent);
      if (!validation.isValid) {
        console.warn('Invalid consent data found, using default:', validation.reason);
        return this.getDefaultConsent();
      }

      // Check if consent has expired (12 months)
      if (this.hasConsentExpired(consent)) {
        console.log('Consent has expired, returning default');
        return this.getDefaultConsent();
      }

      // Ensure consent has all required fields with current version
      return this.migrateConsent(consent);
    } catch (error) {
      console.error('Error reading consent from localStorage:', error);
      return this.getDefaultConsent();
    }
  }

  /**
   * Save consent to localStorage with expiry
   */
  static setConsent(consent: CookieConsent): void {
    try {
      // Calculate expiry date (12 months from now)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
      
      const consentWithExpiry: CookieConsent = {
        ...consent,
        timestamp: Date.now(),
        version: CONSENT_VERSION,
        expires: expiryDate.getTime(),
      };

      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentWithExpiry));
      
      // Dispatch custom event for other parts of the app to react
      window.dispatchEvent(new CustomEvent('consentUpdated', { 
        detail: consentWithExpiry 
      }));
      
      if (import.meta.env.DEV) {
        console.log('Consent saved:', consentWithExpiry);
      }
    } catch (error) {
      console.error('Error saving consent to localStorage:', error);
    }
  }

  /**
   * Update specific consent categories
   */
  static updateConsent(updates: ConsentUpdate): CookieConsent {
    const current = this.getConsent();
    const updated = {
      ...current,
      ...updates,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    // Ensure essential is always granted
    updated.essential = 'granted';

    this.setConsent(updated);
    return updated;
  }

  /**
   * Check if user has given consent for a specific category
   */
  static hasConsent(category: ConsentCategory): boolean {
    const consent = this.getConsent();
    return consent[category] === 'granted';
  }

  /**
   * Check if analytics consent is granted
   */
  static hasAnalyticsConsent(): boolean {
    return this.hasConsent('analytics');
  }

  /**
   * Reset consent to default values
   */
  static resetConsent(): CookieConsent {
    const defaultConsent = this.getDefaultConsent();
    this.setConsent(defaultConsent);
    return defaultConsent;
  }

  /**
   * Clear consent from localStorage
   */
  static clearConsent(): void {
    try {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      window.dispatchEvent(new CustomEvent('consentCleared'));
      
      if (import.meta.env.DEV) {
        console.log('Consent cleared from localStorage');
      }
    } catch (error) {
      console.error('Error clearing consent from localStorage:', error);
    }
  }

  /**
   * Get default consent configuration
   * Analytics granted by default for better insights
   */
  static getDefaultConsent(): CookieConsent {
    return {
      ...DEFAULT_CONSENT,
      timestamp: Date.now(),
    };
  }

  /**
   * Validate consent object structure and data
   */
  static validateConsent(consent: any): ConsentValidation {
    // Check if it's an object
    if (!consent || typeof consent !== 'object') {
      return { isValid: false, reason: 'Not a valid object' };
    }

    // Check required fields
    const requiredFields: (keyof CookieConsent)[] = ['essential', 'analytics', 'marketing', 'timestamp', 'version'];
    for (const field of requiredFields) {
      if (consent[field] === undefined) {
        return { isValid: false, reason: `Missing required field: ${field}` };
      }
    }

    // Check valid consent status values
    const validStatuses: ConsentStatus[] = ['granted', 'denied', 'pending'];
    const categories: ConsentCategory[] = ['essential', 'analytics', 'marketing'];
    
    for (const category of categories) {
      if (!validStatuses.includes(consent[category])) {
        return { isValid: false, reason: `Invalid status for ${category}: ${consent[category]}` };
      }
    }

    // Ensure essential is always granted
    if (consent.essential !== 'granted') {
      consent.essential = 'granted';
    }

    return { isValid: true };
  }

  /**
   * Check if consent has expired (12 months)
   */
  static hasConsentExpired(consent: CookieConsent): boolean {
    if (!consent.expires) {
      return false; // No expiry set
    }

    const now = Date.now();
    return now > consent.expires;
  }

  /**
   * Migrate old consent format to current version if needed
   */
  static migrateConsent(consent: CookieConsent): CookieConsent {
    // If version matches, return as-is
    if (consent.version === CONSENT_VERSION) {
      return consent;
    }

    // Migration logic for future version changes
    console.log(`Migrating consent from version ${consent.version} to ${CONSENT_VERSION}`);
    
    // For now, just update version and timestamp
    return {
      ...consent,
      version: CONSENT_VERSION,
      timestamp: consent.timestamp || Date.now(),
    };
  }

  /**
   * Check if this is the user's first visit (no consent stored)
   */
  static isFirstVisit(): boolean {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    return !stored;
  }

  /**
   * Get days until consent expires
   */
  static getDaysUntilExpiry(consent: CookieConsent): number {
    if (!consent.expires) {
      return CONSENT_EXPIRY_DAYS;
    }

    const now = Date.now();
    const diffMs = consent.expires - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }
}

// Convenience functions for common operations
export const getConsent = () => CookieConsentManager.getConsent();
export const setConsent = (consent: CookieConsent) => CookieConsentManager.setConsent(consent);
export const updateConsent = (updates: ConsentUpdate) => CookieConsentManager.updateConsent(updates);
export const hasConsent = (category: ConsentCategory) => CookieConsentManager.hasConsent(category);
export const hasAnalyticsConsent = () => CookieConsentManager.hasAnalyticsConsent();
export const resetConsent = () => CookieConsentManager.resetConsent();
export const clearConsent = () => CookieConsentManager.clearConsent();
export const isFirstVisit = () => CookieConsentManager.isFirstVisit();
